import { useEffect, useState, useCallback } from "react";
import {
  ArrowRight,
  MapPin,
  Shield,
  Award,
  Sparkles,
  Star,
  ShoppingBag,
  Quote,
  Truck,
  Phone,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/19127451/pexels-photo-19127451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1920";

const APPAREL_IMG = "/products/work-hoodie.jpg";
const KAC_IMG = "/products/keep-austin-cut-vneck.jpg";
const HW_IMG =
  "https://images.pexels.com/photos/19127451/pexels-photo-19127451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1920";
const DEBRIS_IMG =
  "https://images.pexels.com/photos/9373714/pexels-photo-9373714.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1920";

const SLIDES = ["main", "halloween", "debris", "testimonial", "apparel", "keep-austin-cut"];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;

  const go = useCallback((delta) => setIdx((i) => (i + delta + total) % total), [total]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 7000);
    return () => clearInterval(t);
  }, [total]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden grain"
    >
      {/* Slides */}
      <div
        className="absolute inset-0 flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {/* SLIDE 1 — Main hero */}
        <div className="relative w-full h-full flex-shrink-0">
          <img
            src={HERO_IMG}
            alt="Sleek modern home with green yard"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />

          <div className="container-ic relative h-full flex items-end pb-20 pt-40 md:pb-28 md:pt-44">
            <div className="max-w-4xl reveal">
              <div className="overline mb-8" data-testid="hero-veteran-tag">
                <Shield className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
                Veteran Owned
              </div>
              <h1
                className="font-display text-white text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tighter"
                data-testid="hero-headline"
              >
                Premier yard
                <br />
                <span className="text-primary">care</span>, done
                <br />
                ironclad.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                From mowing to clean-ups, we sharpen curb appeal across San Antonio and beyond.
                Your yard deserves the Ironclad touch.
              </p>
              <div className="mt-6 inline-flex items-center gap-3 border-l-2 border-primary pl-4 py-1">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-display text-base md:text-lg uppercase tracking-[0.15em] text-white">
                  Protecting your yard the <span className="text-primary">Ironclad</span> way.
                </span>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a href="#booking" className="btn-primary group" data-testid="hero-book-btn">
                  Get Free Estimate
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="#services" className="btn-outline">
                  View Services
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 2 — Halloween Special */}
        <div className="relative w-full h-full flex-shrink-0 bg-black">
          <img
            src={HW_IMG}
            alt="Clean yard with Halloween decorations"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
          <div
            className="absolute inset-0 mix-blend-screen opacity-35"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 25% 40%, rgba(255,140,0,0.25), transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(127,176,105,0.2), transparent 55%)",
            }}
          />

          <div className="container-ic relative h-full flex items-end pb-20 pt-40 md:pb-28 md:pt-44">
            <div className="max-w-4xl reveal">
              <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black mb-6 animate-pulse">
                <Sparkles className="h-3.5 w-3.5" />
                Halloween Special · October
              </div>
              <h1
                className="font-display italic text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.88] tracking-tighter"
                data-testid="hero-halloween"
              >
                Don&apos;t let your yard <br />
                be the <span className="text-primary">scariest</span> <br />
                thing on the block.
              </h1>
              <p className="mt-7 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
                Mowing · hedge trimming · leaf cleanup · mulch installation ·
                property cleanups. Serving San Antonio &amp; surrounding areas.
                <span className="text-primary italic font-bold"> We kill weeds, not vibes.</span>
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="#halloween"
                  className="btn-primary group"
                  data-testid="hero-halloween-cta"
                >
                  See the Halloween Special
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="tel:5129480676" className="btn-outline">
                  <Phone className="h-4 w-4" /> Call 512-948-0676
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 3 — Heavy Debris Removal */}
        <div className="relative w-full h-full flex-shrink-0 bg-black">
          <img
            src={DEBRIS_IMG}
            alt="Heavy debris and site prep removal"
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
          <div
            className="absolute inset-0 mix-blend-screen opacity-25"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 70% 35%, rgba(127,176,105,0.32), transparent 55%)",
            }}
          />

          <div className="container-ic relative h-full flex items-end pb-20 pt-40 md:pb-28 md:pt-44">
            <div className="max-w-4xl reveal">
              <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black mb-6">
                <Truck className="h-3.5 w-3.5" />
                New · Heavy Debris &amp; Junk Removal
              </div>
              <h1
                className="font-display italic text-white text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.88] tracking-tighter"
                data-testid="hero-debris"
              >
                Remodel mess? <br />
                <span className="text-primary">Hauled.</span> Gone. <br />
                Same week.
              </h1>
              <p className="mt-7 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
                Home-remodel site prep · trees, brush &amp; limbs · concrete and
                construction debris · full property junk haul-off. Call or email
                for a custom quote.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="tel:5129480676"
                  className="btn-primary group"
                  data-testid="hero-debris-call"
                >
                  <Phone className="h-4 w-4" />
                  Call 512-948-0676
                </a>
                <a
                  href="mailto:ironcladatx@gmail.com?subject=Heavy%20Debris%20%26%20Junk%20Removal%20Quote&body=Hi%20Ironclad%2C%20I%27d%20like%20a%20quote%20for%20heavy%20debris%20removal.%0A%0AProperty%20address%3A%20%5B%5D%0AScope%2Fdetails%3A%20%5B%5D%0AIdeal%20timeline%3A%20%5B%5D%0AName%3A%20%5B%5D%0APhone%3A%20%5B%5D"
                  className="btn-outline"
                  data-testid="hero-debris-email"
                >
                  Email for Quote
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 4 — Testimonial */}
        <div
          className="relative w-full h-full flex-shrink-0 bg-black"
        >
          <img
            src={HERO_IMG}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black" />
          <div
            className="absolute inset-0 opacity-25 mix-blend-screen"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 30% 40%, rgba(127,176,105,0.35), transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(127,176,105,0.18), transparent 65%)",
            }}
          />

          <div className="container-ic relative h-full flex items-center pt-32 pb-20">
            <div className="max-w-4xl mx-auto text-center reveal">
              <div className="overline mb-6">
                <Star className="inline h-3.5 w-3.5 mr-2 -mt-0.5 fill-primary text-primary" />
                Customer Story
              </div>
              <Quote className="h-12 w-12 text-primary/30 mx-auto mb-6" />
              <p
                className="font-display text-3xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-white"
                data-testid="hero-testimonial"
              >
                &ldquo;Sharpest <span className="text-primary">lawn</span> on the block.
                Discipline, consistency, curb appeal.&rdquo;
              </p>
              <div className="mt-8 inline-flex flex-col items-center">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <div className="h-px w-8 bg-primary mb-3" />
                <div className="font-display text-lg font-bold uppercase tracking-widest text-white">
                  Aaron P.
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-primary mt-1">
                  New Braunfels, TX
                </div>
              </div>
              <div className="mt-10">
                <a href="#booking" className="btn-primary">
                  Join the Family
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 5 — Apparel */}
        <div
          className="relative w-full h-full flex-shrink-0 bg-black"
        >
          <img
            src={APPAREL_IMG}
            alt="Ironclad apparel"
            className="absolute inset-0 w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

          <div className="container-ic relative h-full flex items-end pb-20 pt-40 md:pb-28 md:pt-44">
            <div className="max-w-4xl reveal">
              <div className="overline mb-6">
                <ShoppingBag className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
                Ironclad Apparel
              </div>
              <h2
                className="font-display text-white text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tighter"
                data-testid="hero-apparel"
              >
                Gear that
                <br />
                <span className="text-primary">works</span> as hard
                <br />
                as you do.
              </h2>
              <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                Sporty, outdoorsman apparel in jet black and Ironclad green. Built for
                Texas heat. Embroidered for those who don&apos;t quit.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#keep-austin-cut"
                  className="btn-primary group"
                  data-testid="hero-apparel-order"
                >
                  Order Now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="/shop" className="btn-outline">
                  Shop All Gear
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SLIDE 6 — Keep Austin Cut Active-Wear */}
        <div className="relative w-full h-full flex-shrink-0 bg-black">
          {/* Decorative bg */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 70% 40%, rgba(127,176,105,0.25), transparent 60%), radial-gradient(ellipse at 25% 80%, rgba(127,176,105,0.12), transparent 65%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black" />

          <div className="container-ic relative h-full grid grid-cols-1 lg:grid-cols-2 gap-10 items-center pt-32 md:pt-36 pb-20">
            {/* Left copy */}
            <div className="reveal order-2 lg:order-1">
              <div className="overline mb-6">
                <ShoppingBag className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
                Just Dropped · Active-Wear
              </div>
              <h2
                className="font-display italic text-white text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tighter"
                data-testid="hero-kac"
              >
                Keep
                <br />
                <span className="text-primary">Austin</span>
                <br />
                Cut.
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
                The official Ironclad Active-Wear V-neck. Moisture-wicking,
                slim-fit, built for Texas summer.
              </p>
              <div className="mt-3 font-display text-2xl md:text-3xl font-black text-primary">
                $32
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#keep-austin-cut"
                  className="btn-primary group"
                  data-testid="hero-kac-order"
                >
                  Order Now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="/shop#apparel" className="btn-outline">
                  Shop Apparel
                </a>
              </div>
            </div>

            {/* Right image */}
            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative aspect-square w-full max-w-md">
                <div className="absolute inset-0 border border-primary/40 translate-x-3 translate-y-3 pointer-events-none" />
                <div className="relative w-full h-full overflow-hidden bg-white">
                  <img
                    src={KAC_IMG}
                    alt="Keep Austin Cut V-neck active-wear tee"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Static stats / dots at bottom — shared across slides */}
      <div className="absolute inset-x-0 bottom-0 pb-6 md:pb-8 z-20 pointer-events-none">
        <div className="container-ic flex items-end justify-between gap-6">
          {/* Dots */}
          <div className="flex items-center gap-2 pointer-events-auto">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 transition-all ${
                  i === idx ? "w-10 bg-primary" : "w-4 bg-white/30 hover:bg-white/60"
                }`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>

          <div className="hidden md:grid grid-cols-4 gap-6 lg:gap-10 max-w-2xl">
            {[
              { icon: Shield, label: "Veteran", sub: "Owned" },
              { icon: Award, label: "Premier", sub: "Service" },
              { icon: MapPin, label: "SATX+", sub: "Service Area" },
              { icon: ArrowRight, label: "10% Off", sub: "Vet Discount" },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="border-l border-primary/30 pl-3 backdrop-blur-sm"
                  data-testid={`hero-stat-${i}`}
                >
                  <Icon className="h-3.5 w-3.5 text-primary mb-1" />
                  <div className="font-display text-white text-lg md:text-xl font-bold leading-tight">
                    {s.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">
                    {s.sub}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px divider-line z-10" />
    </section>
  );
}
